import React, { Fragment, useCallback, useState } from 'react'
import { Disclosure } from '@headlessui/react'
import {
  Bars3Icon,
  BuildingStorefrontIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { removeAuthorizationToken } from '../authentication/storage'
import { NavLink } from 'react-router-dom'
import classNames from 'classnames'
import logo from '../../assets/images/packoorangLogo.svg'
import SecondaryButton from '../../components/Buttons/SecondaryButton'
import { useBusinessLocation } from '../../context/BusinessLocationContext'
import { SelectLocationModal } from '../locations/SelectLocationModal'

interface AppLayoutProps {
  children?: React.ReactNode
}

export const AppShell: React.FC<AppLayoutProps> = ({ children }) => {
  const [changeLocationModalOpen, setChangeLocationModalOpen] = useState(false)

  const handleLogout = useCallback(() => {
    removeAuthorizationToken()
    window.location.reload()
  }, [])

  const navigation = [
    { name: 'Scan', href: '/scan' },
    { name: 'Analytics', href: '/analytics' },
    { name: 'Parcels', href: '/parcels' },
  ]

  const { selectedLocation } = useBusinessLocation()

  return (
    <>
      <SelectLocationModal
        open={changeLocationModalOpen || !selectedLocation}
        onClose={() => setChangeLocationModalOpen(false)}
      />
      <div className="min-h-screen bg-gray-50">
        <Disclosure as="nav" className="border-b border-gray-200 bg-white">
          {({ open }) => (
            <>
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 justify-between">
                  <div className="flex">
                    <div className="flex flex-shrink-0 items-center">
                      <img
                        className="block h-8 w-auto lg:hidden"
                        src={logo}
                        alt="Packoorang logo"
                      />
                      <img
                        className="hidden h-8 w-auto lg:block"
                        src={logo}
                        alt="Packoorang logo"
                      />
                    </div>
                    {/* Left side non-mobile */}
                    <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                      {navigation.map(item => (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={({ isActive }) =>
                            classNames(
                              isActive
                                ? 'border-emerald-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                              'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium'
                            )
                          }
                        >
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  </div>

                  {/* Right side non-mobile */}
                  <div className="hidden sm:ml-6 sm:flex sm:items-center">
                    {/* */}
                    <SecondaryButton
                      className={'flex gap-2 items-center text-gray-500'}
                      onClick={() => setChangeLocationModalOpen(true)}
                    >
                      <BuildingStorefrontIcon
                        className="h-6 w-6 text-gray-500"
                        aria-hidden="true"
                      />
                      <span className={'text-gray-500'}>
                        {selectedLocation?.name ?? 'Select location'}
                      </span>
                    </SecondaryButton>

                    <button
                      onClick={() => handleLogout()}
                      className={'text-gray-500 ml-8'}
                    >
                      Sign out
                    </button>
                    {/* Log out desktop */}
                  </div>
                  <div className="-mr-2 flex items-center sm:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2">
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      ) : (
                        <Bars3Icon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="sm:hidden">
                <div className="space-y-1 pb-3 pt-2">
                  {navigation.map(item => (
                    <Disclosure.Button key={item.href} as={Fragment}>
                      <NavLink
                        to={item.href}
                        key={item.name}
                        className={({ isActive }) =>
                          classNames(
                            isActive
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800',
                            'block border-l-4 py-2 pl-3 pr-4 text-base font-medium'
                          )
                        }
                      >
                        {item.name}
                      </NavLink>
                    </Disclosure.Button>
                  ))}
                </div>

                <div className="border-t border-gray-200 pb-3 pt-4">
                  <div className="space-y-1">
                    <Disclosure.Button
                      as={'button'}
                      className={
                        'flex px-4 py-2 text-base font-medium text-gray-500 gap-2'
                      }
                      onClick={() => setChangeLocationModalOpen(true)}
                    >
                      <BuildingStorefrontIcon className={'h-6 w-6'} />
                      <span>{selectedLocation?.name ?? 'Select location'}</span>
                    </Disclosure.Button>
                    <Disclosure.Button
                      as={'div'}
                      className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      onClick={() => handleLogout()}
                    >
                      Sign out
                    </Disclosure.Button>
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <div className="py-10">
          <main>
            <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">{children}</div>
          </main>
        </div>
      </div>
    </>
  )
}
